import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, ArrowRight, Check, GraduationCap, Calendar, MessageSquare, BookOpen, Star, Clock, Search, Sparkles, Award } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { bookingApi } from '@/lib/bookingApi';
import { toast } from 'sonner';

const PROGRAMS = ['IELTS', 'GMAT', 'VCE', 'Selective Entry', 'CBC', 'Coding', 'Other'];
const LESSON_TYPES = ['1-on-1 tutoring', 'exam preparation', 'essay feedback session', 'speaking practice', 'trial lesson'];
const DURATION = 60;  // Locked to 60 minutes (Phase 4D polish)
const LESSON_MODE = 'online';

const MEL_TZ = 'Australia/Melbourne';
const fmtMel = (iso) => {
  try {
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: MEL_TZ, weekday: 'short', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(new Date(iso));
  } catch (_e) { return iso; }
};
const fmtLocal = (iso) => {
  try {
    return new Intl.DateTimeFormat('en-AU', {
      weekday: 'short', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(new Date(iso));
  } catch (_e) { return iso; }
};
const localTzName = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (_e) { return 'Local'; } })();

const Step = ({ num, label, active, done }) => (
  <div className={`flex items-center gap-2 ${active ? 'text-red-700' : done ? 'text-emerald-700' : 'text-gray-400'}`}>
    <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold ${active ? 'bg-red-700 text-white' : done ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
      {done ? <Check className="h-3 w-3" /> : num}
    </div>
    <span className="text-[10px] uppercase tracking-wider font-bold hidden sm:inline">{label}</span>
  </div>
);

const Btn = ({ children, onClick, primary, disabled, testid }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testid}
    className={`h-10 px-5 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40 transition ${
      primary ? 'bg-red-700 hover:bg-red-800 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100'
    }`}>
    {children}
  </button>
);

const StudentBookingPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [step, setStep] = useState(1);
  const [program, setProgram] = useState('IELTS');
  const [lessonType, setLessonType] = useState('1-on-1 tutoring');
  const [subject, setSubject] = useState('');
  const [duration] = useState(DURATION);  // locked
  const [tutorFilter, setTutorFilter] = useState('');
  const [tutors, setTutors] = useState([]);
  const [loadingTutors, setLoadingTutors] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [matchMe, setMatchMe] = useState(false);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [chosenSlot, setChosenSlot] = useState(null);
  const [preferredWindow, setPreferredWindow] = useState('');  // optional free-text for match-me / tutor-no-time
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  // True when the chosen flow does not require a time-slot selection:
  //  - matchMe (Winhouse-matched, admin picks tutor + time)
  //  - tutor selected but has no available slots (request-tutor-no-time)
  const tutorHasSlots = matchMe ? true : Boolean(selectedTutor?.has_availability);
  const noTimeFlow = matchMe || (selectedTutor && !selectedTutor.has_availability);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/student/login', { replace: true, state: { from: { pathname: '/student/book' } } }); return; }
    const role = (user.role || '').toLowerCase();
    if (role !== 'student' && role !== 'parent') {
      toast.error('Only students can use self-service booking.');
      navigate('/', { replace: true });
    }
  }, [loading, user, navigate]);

  const loadTutors = useCallback(async (prog) => {
    setLoadingTutors(true);
    try { const r = await bookingApi.listTutors({ program: prog }); setTutors(r.items || []); }
    catch (_e) { toast.error('Could not load tutors.'); }
    finally { setLoadingTutors(false); }
  }, []);

  useEffect(() => { if (step === 2) loadTutors(program); }, [step, program, loadTutors]);

  const loadSlots = useCallback(async () => {
    setLoadingSlots(true);
    try {
      if (matchMe) {
        // Aggregate slots from every tutor (dedupe by start time)
        const tutorList = tutors.length ? tutors : (await bookingApi.listTutors({ program })).items;
        const all = await Promise.all(tutorList.map((t) => bookingApi.tutorAvailability(t.tutor_email, { days: 14, duration_min: duration }).then((r) => (r.slots || []).map((s) => ({ ...s, tutor_email: t.tutor_email }))).catch(() => [])));
        const flat = all.flat();
        const map = new Map();
        flat.forEach((s) => { if (!map.has(s.start)) map.set(s.start, s); });
        setSlots(Array.from(map.values()).sort((a, b) => a.start.localeCompare(b.start)));
      } else if (selectedTutor) {
        const r = await bookingApi.tutorAvailability(selectedTutor.tutor_email, { days: 14, duration_min: duration });
        setSlots(r.slots || []);
      }
    } catch (_e) { toast.error('Could not load availability.'); }
    finally { setLoadingSlots(false); }
  }, [selectedTutor, duration, matchMe, tutors, program]);

  useEffect(() => { if (step === 3 && !noTimeFlow) loadSlots(); }, [step, loadSlots, noTimeFlow]);

  // Auto-skip step 3 (time picker) when the chosen flow does not require it.
  // We keep step 3 in the URL so the stepper visual stays consistent.
  useEffect(() => {
    if (step === 3 && noTimeFlow) {
      setChosenSlot(null);
      setStep(4);
    }
  }, [step, noTimeFlow]);

  const submit = async () => {
    if (!noTimeFlow && !chosenSlot) return;
    if (!selectedTutor && !matchMe) return;
    setSubmitting(true);
    try {
      // Three flows:
      //  1) Standard booking — tutor + chosen slot → POST /api/booking/request
      //  2) Winhouse-matched — no tutor, optional time → POST /api/booking/request-match
      //  3) Tutor-no-time   — tutor picked but no slots → POST /api/booking/request-tutor-no-time
      const base = {
        program,
        lesson_type: lessonType,
        subject: subject || `${program} session`,
        duration_min: duration,
        student_notes: notes.trim() || null,
      };
      let r;
      if (matchMe) {
        r = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/booking/request-match`,
          { ...base, preferred_window: preferredWindow.trim() || null },
          { withCredentials: true },
        ).then((x) => x.data);
      } else if (selectedTutor && !selectedTutor.has_availability) {
        r = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/booking/request-tutor-no-time`,
          { ...base, tutor_email: selectedTutor.tutor_email, preferred_window: preferredWindow.trim() || null },
          { withCredentials: true },
        ).then((x) => x.data);
      } else {
        r = await bookingApi.createRequest({
          ...base,
          tutor_email: selectedTutor.tutor_email,
          start_time: chosenSlot.start,
        });
      }
      setDone(r);
      toast.success(
        matchMe ? 'Match request sent to Winhouse.' :
        (selectedTutor && !selectedTutor.has_availability) ? 'Request sent — tutor will propose a time.' :
        'Booking request sent.'
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create booking.');
    } finally { setSubmitting(false); }
  };

  const filteredTutors = tutors.filter((t) => !tutorFilter || (t.tutor_name || '').toLowerCase().includes(tutorFilter.toLowerCase()) || (t.subjects || '').toLowerCase().includes(tutorFilter.toLowerCase()));

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-6 w-6 animate-spin text-red-700" /></div>;

  if (done) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center p-6" data-testid="booking-success">
        <div className="bg-white border border-gray-200 rounded-2xl p-10 max-w-lg w-full text-center shadow-sm">
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4"><Check className="h-6 w-6 text-emerald-700" /></div>
          <div className="text-2xl font-bold text-gray-900">
            {done.request_status === 'approved' ? 'Booking confirmed' :
             done.request_status === 'pending_matching' ? 'Match request sent' :
             done.request_status === 'pending_tutor_time' ? 'Request sent to tutor' :
             'Booking request sent'}
          </div>
          <p className="text-sm text-gray-600 mt-3">
            {done.request_status === 'approved' ? (
              <>Your class with <strong>{done.tutor_name}</strong> is confirmed for {fmtMel(done.start_time)} (Melbourne time).</>
            ) : done.request_status === 'pending_matching' ? (
              <>Your request has been sent to Winhouse. Our team will match you with the best available tutor and notify you once confirmed.</>
            ) : done.request_status === 'pending_tutor_time' ? (
              <>We sent your request to <strong>{done.tutor_name}</strong>. They'll propose a time that suits both of you and you'll be notified once confirmed.</>
            ) : (
              <>We sent your request to <strong>{done.tutor_name}</strong>. You'll get an email as soon as they respond.</>
            )}
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 text-left text-xs bg-gray-50 rounded-xl p-4">
            <div><span className="text-gray-500">Subject</span><div className="font-bold">{done.subject}</div></div>
            <div><span className="text-gray-500">Duration</span><div className="font-bold">{done.duration_min} min</div></div>
            <div><span className="text-gray-500">Tutor</span><div className="font-bold">{done.tutor_name}</div></div>
            <div><span className="text-gray-500">Status</span><div className="font-bold uppercase">{done.request_status}</div></div>
          </div>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <button onClick={() => navigate('/student/dashboard')} className="h-10 px-5 rounded-full bg-black text-white text-xs font-bold uppercase tracking-wider" data-testid="goto-dashboard">My dashboard</button>
            <button onClick={() => { setDone(null); setStep(1); setChosenSlot(null); setSelectedTutor(null); setMatchMe(false); setPreferredWindow(''); setNotes(''); }} className="h-10 px-5 rounded-full bg-white border border-gray-200 text-xs font-bold uppercase tracking-wider" data-testid="book-another">Book another</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="student-booking-page">
      <header className="bg-black text-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <GraduationCap className="h-5 w-5 text-[#D3D86F]" />
            <div>
              <div className="text-[10px] uppercase tracking-[3px] text-[#D3D86F] font-bold">WINHOUSE TUTORS</div>
              <div className="text-sm font-bold">Book a Class</div>
            </div>
          </div>
          <button onClick={() => navigate('/student/dashboard')} className="h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid="exit-booking"><ArrowLeft className="h-3 w-3" /> Dashboard</button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-6">
        <div className="flex items-center gap-4 mb-6 bg-white border border-gray-200 rounded-2xl p-4" data-testid="booking-stepper">
          <Step num={1} label="Program" active={step === 1} done={step > 1} />
          <div className="flex-1 h-px bg-gray-200" />
          <Step num={2} label="Tutor" active={step === 2} done={step > 2} />
          <div className="flex-1 h-px bg-gray-200" />
          <Step num={3} label="Time" active={step === 3} done={step > 3} />
          <div className="flex-1 h-px bg-gray-200" />
          <Step num={4} label="Confirm" active={step === 4} done={false} />
        </div>

        {step === 1 && (
          <section className="bg-white border border-gray-200 rounded-2xl p-6 space-y-5" data-testid="step-program">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Program</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {PROGRAMS.map((p) => (
                  <button key={p} onClick={() => setProgram(p)} data-testid={`program-${p}`}
                    className={`h-12 rounded-xl text-xs font-bold uppercase tracking-wider transition ${program === p ? 'bg-black text-[#D3D86F] border-2 border-[#D3D86F]' : 'bg-white border border-gray-200 hover:border-gray-400'}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Lesson type</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {LESSON_TYPES.map((t) => (
                  <button key={t} onClick={() => setLessonType(t)} data-testid={`lesson-${t.replace(/\W+/g, '-')}`}
                    className={`h-11 rounded-xl text-xs font-semibold capitalize transition px-3 text-left ${lessonType === t ? 'bg-red-50 border-2 border-red-700 text-red-900' : 'bg-white border border-gray-200 hover:border-gray-400'}`}>
                    <BookOpen className="h-3.5 w-3.5 inline mr-2" />{t}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Subject focus (optional)</div>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={`e.g. ${program} Task 2 writing`}
                className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid="step-subject" />
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Duration · Mode</div>
              <div className="flex flex-wrap gap-2 items-center text-xs text-gray-700">
                <span className="px-3 h-10 inline-flex items-center rounded-full bg-black text-white font-bold uppercase tracking-wider" data-testid="duration-locked">60 min</span>
                <span className="px-3 h-10 inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 font-bold uppercase tracking-wider" data-testid="mode-locked">Online</span>
                <span className="text-[10px] text-gray-400 italic">All Winhouse classes are 60-minute online sessions for the current MVP.</span>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Btn primary onClick={() => setStep(2)} testid="step-next-1">Next: Choose tutor <ArrowRight className="h-3 w-3" /></Btn>
            </div>
          </section>
        )}

        {step === 2 && (
          <section className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4" data-testid="step-tutor">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Choose tutor · {program}</div>
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-3 h-9">
                <Search className="h-3.5 w-3.5 text-gray-400" />
                <input value={tutorFilter} onChange={(e) => setTutorFilter(e.target.value)} placeholder="Filter…" className="bg-transparent text-xs focus:outline-none w-32 sm:w-48" data-testid="tutor-filter" />
              </div>
            </div>
            {loadingTutors ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={() => { setMatchMe(true); setSelectedTutor(null); }}
                  data-testid="match-me-card"
                  className={`w-full text-left rounded-2xl p-4 border-2 transition flex items-center gap-4 ${matchMe ? 'border-[#D3D86F] bg-black text-white' : 'border-gray-200 bg-gradient-to-r from-[#FAF9F6] to-amber-50 hover:border-amber-300'}`}>
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${matchMe ? 'bg-[#D3D86F] text-black' : 'bg-amber-500 text-white'}`}><Sparkles className="h-6 w-6" /></div>
                  <div className="flex-1">
                    <div className={`text-sm font-bold ${matchMe ? 'text-[#D3D86F]' : 'text-gray-900'}`}>Let Winhouse match me with the best tutor</div>
                    <div className={`text-[11px] mt-0.5 ${matchMe ? 'text-white/70' : 'text-gray-600'}`}>Our team picks the right verified tutor for your subject + level. No specific tutor selection required.</div>
                  </div>
                  {matchMe && <Check className="h-5 w-5 text-[#D3D86F]" />}
                </button>

                <div className="flex items-center gap-3"><div className="flex-1 h-px bg-gray-200" /><div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">or pick a verified tutor</div><div className="flex-1 h-px bg-gray-200" /></div>

                {filteredTutors.length === 0 ? (
                  <div className="text-center text-xs text-gray-400 italic py-10" data-testid="no-tutors">No tutors match this program. Try "Let Winhouse match me" above.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tutor-grid">
                    {filteredTutors.map((t) => (
                      <button key={t.tutor_email} onClick={() => { setSelectedTutor(t); setMatchMe(false); }} data-testid={`tutor-${t.tutor_email}`}
                        className={`text-left bg-white rounded-2xl p-4 border-2 transition ${!matchMe && selectedTutor?.tutor_email === t.tutor_email ? 'border-red-700 ring-2 ring-red-100' : 'border-gray-200 hover:border-gray-400'}`}>
                        <div className="flex items-start gap-3">
                          {t.profile_photo_url ? (
                            <img src={t.profile_photo_url} alt={t.tutor_name} className="h-12 w-12 rounded-xl object-cover" />
                          ) : (
                            <div className="h-12 w-12 rounded-xl bg-black text-[#D3D86F] flex items-center justify-center font-bold text-lg">{(t.tutor_name || '?').slice(0, 1).toUpperCase()}</div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <div className="text-sm font-bold text-gray-900 truncate">{t.tutor_name}</div>
                              {t.verified && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold uppercase tracking-wider flex items-center gap-0.5"><Award className="h-2.5 w-2.5" />Verified</span>}
                            </div>
                            <div className="text-[11px] text-gray-500 truncate">{t.subjects || '—'}</div>
                            {t.qualifications && <div className="text-[10px] text-gray-500 truncate mt-0.5"><strong>Qual:</strong> {t.qualifications}</div>}
                          </div>
                          {t.hourly_rate_aud && <div className="text-[11px] font-bold text-emerald-700 whitespace-nowrap">A${t.hourly_rate_aud}/h</div>}
                        </div>
                        {t.bio && <div className="text-[11px] text-gray-600 mt-2 line-clamp-2">{t.bio}</div>}
                        <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-500 flex-wrap">
                          <span className="flex items-center gap-0.5"><Star className="h-3 w-3 text-amber-400" fill="currentColor" />{t.rating_placeholder}</span>
                          {t.years_experience != null && <span>{t.years_experience}y experience</span>}
                          {t.has_availability ? (
                            <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold uppercase tracking-wider">Available</span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold uppercase tracking-wider" data-testid={`tutor-no-slots-${t.tutor_email}`}>No current slots — request anyway</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-between pt-2">
              <Btn onClick={() => setStep(1)} testid="step-back-2"><ArrowLeft className="h-3 w-3" /> Back</Btn>
              <Btn primary disabled={!selectedTutor && !matchMe} onClick={() => setStep(3)} testid="step-next-2">
                {noTimeFlow ? 'Next: Confirm' : 'Next: Pick time'} <ArrowRight className="h-3 w-3" />
              </Btn>
            </div>
          </section>
        )}

        {step === 3 && !noTimeFlow && (
          <section className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4" data-testid="step-time">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Pick a slot · {selectedTutor?.tutor_name} · 60 min · <span className="text-red-700">Your local: {localTzName}</span> · <span className="text-gray-700">Melbourne shown second</span></div>
            {loadingSlots ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div>
            ) : slots.length === 0 ? (
              <div className="space-y-4" data-testid="no-slots">
                <div className="text-center bg-amber-50 border border-amber-200 rounded-2xl p-6">
                  <Sparkles className="h-6 w-6 text-amber-600 mx-auto mb-2" />
                  <div className="text-sm font-bold text-gray-900">No suitable times available?</div>
                  <div className="text-[11px] text-gray-600 mt-1 mb-4">Ask Winhouse to arrange a tutor for you. Our team will find the best match and propose times that suit your schedule.</div>
                  <button onClick={() => { setMatchMe(true); setSelectedTutor(null); setStep(4); }}
                    className="h-10 px-5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
                    data-testid="cta-request-winhouse-match">
                    <Sparkles className="h-3 w-3" /> Request Winhouse matching
                  </button>
                </div>
                {selectedTutor && (
                  <div className="text-center bg-white border border-gray-200 rounded-2xl p-5">
                    <div className="text-[11px] text-gray-600 mb-3">Or send your request to <strong>{selectedTutor.tutor_name}</strong> anyway — they'll propose a time that works for you.</div>
                    <button onClick={() => { setSelectedTutor({ ...selectedTutor, has_availability: false }); setStep(4); }}
                      className="h-10 px-5 rounded-full bg-black text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
                      data-testid="cta-request-tutor-no-time">
                      <Clock className="h-3 w-3" /> Ask {selectedTutor.tutor_name.split(' ')[0]} to propose a time
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4" data-testid="slot-calendar">
                {Object.entries(slots.reduce((acc, s) => {
                  const d = new Date(s.start);
                  const dayKey = new Intl.DateTimeFormat('en-AU', { weekday: 'long', day: '2-digit', month: 'short' }).format(d);
                  (acc[dayKey] = acc[dayKey] || []).push(s);
                  return acc;
                }, {})).map(([day, daySlots]) => (
                  <div key={day} data-testid={`day-${day}`}>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-gray-700 mb-2 pb-1 border-b border-gray-100">{day} <span className="text-gray-400 font-normal">· {daySlots.length} slot{daySlots.length === 1 ? '' : 's'}</span></div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                      {daySlots.map((s) => {
                        const d = new Date(s.start);
                        const localTime = new Intl.DateTimeFormat('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
                        const melTime = new Intl.DateTimeFormat('en-AU', { timeZone: MEL_TZ, hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
                        return (
                          <button key={s.start} onClick={() => setChosenSlot(s)} data-testid={`slot-${s.start}`}
                            className={`min-h-[64px] py-2 px-2 rounded-xl text-[12px] font-bold transition flex flex-col items-center justify-center ${chosenSlot?.start === s.start ? 'bg-red-700 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                            <div>{localTime}</div>
                            <div className={`text-[9px] font-medium ${chosenSlot?.start === s.start ? 'text-white/70' : 'text-gray-400'}`}>MEL · {melTime}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between pt-2">
              <Btn onClick={() => setStep(2)} testid="step-back-3"><ArrowLeft className="h-3 w-3" /> Back</Btn>
              <Btn primary disabled={!chosenSlot} onClick={() => setStep(4)} testid="step-next-3">Next: Confirm <ArrowRight className="h-3 w-3" /></Btn>
            </div>
          </section>
        )}

        {step === 4 && (
          <section className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4" data-testid="step-confirm">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Confirm your booking</div>
            {noTimeFlow && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2" data-testid="no-time-banner">
                <Sparkles className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-[11px] text-amber-900">
                  {matchMe
                    ? <>Winhouse will match you with the best available tutor and propose times that suit you. You'll be notified once a tutor is confirmed.</>
                    : <>This tutor has no open slots right now — they'll propose a time that works for both of you and notify you once confirmed.</>}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-xs bg-gray-50 rounded-xl p-4">
              <Item label="Program" value={program} />
              <Item label="Lesson type" value={lessonType} />
              <Item label="Subject" value={subject || `${program} session`} />
              <Item label="Duration" value={`${duration} min`} />
              <Item label="Tutor" value={matchMe ? '✨ Winhouse will match you' : selectedTutor?.tutor_name} />
              {noTimeFlow ? (
                <Item label="Time" value="To be proposed by Winhouse/tutor" />
              ) : (
                <>
                  <Item label={`Time (${localTzName})`} value={chosenSlot ? fmtLocal(chosenSlot.start) : '—'} />
                  <Item label="Time (Melbourne)" value={chosenSlot ? fmtMel(chosenSlot.start) : '—'} />
                </>
              )}
              <Item label="Mode" value="Online" />
              {!noTimeFlow && selectedTutor?.hourly_rate_aud && <Item label="Estimated price" value={`A$${Math.round((selectedTutor.hourly_rate_aud * duration) / 60 * 100) / 100} (no payment now)`} />}
              <Item label="Approval" value={matchMe ? 'admin matching queue' : (selectedTutor?.has_availability === false ? 'tutor will propose a time' : (selectedTutor?.booking_mode || 'tutor_approval').replace('_', ' '))} />
            </div>
            {noTimeFlow && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 flex items-center gap-1"><Calendar className="h-3 w-3" /> Preferred days / times (optional)</div>
                <input value={preferredWindow} onChange={(e) => setPreferredWindow(e.target.value)} placeholder="e.g. weekday evenings after 6pm AEST"
                  className="w-full h-10 px-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid="preferred-window" />
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1 flex items-center gap-1"><MessageSquare className="h-3 w-3" /> Message {noTimeFlow ? 'to Winhouse' : 'to tutor'} (optional)</div>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder={noTimeFlow ? 'Tell us about your goals, current level, focus areas…' : 'Anything the tutor should know before the class?'}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-red-200" data-testid="step-notes" />
            </div>
            <div className="flex justify-between pt-2">
              <Btn onClick={() => setStep(noTimeFlow ? 2 : 3)} testid="step-back-4"><ArrowLeft className="h-3 w-3" /> Back</Btn>
              <Btn primary disabled={submitting} onClick={submit} testid="step-submit">{submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} {noTimeFlow ? 'Send request' : 'Send booking'}</Btn>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

const Item = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
    <div className="font-bold text-gray-900 mt-0.5">{value || '—'}</div>
  </div>
);

export default StudentBookingPage;
